import React, { forwardRef } from 'react';
import { mergeClassNames } from '../../../../../utils';
import { headerHeight } from '../../../../../utils/menuHeight';
import styles from './index.module.scss';

/**
 * Container component for setting proportional height to component width
 *
 * This component is mostly used to force a component to have equal height as the hero image
 *
 * @example
 * ```javascript
 * <ButtonSection>
 *   <Fixed overlay={<BuyRoomButton bundles={bundles} />} >
 *     <HeightElem aspectRatio={heroImage?.fluid?.aspectRatio} />
 *   </Fixed>
 * </ButtonSection>
 * ```
 *
 * @param {object} param
 * @param {number} param.aspectRatio
 * @param {boolean} [param.isVisible]
 * @param {boolean} [param.debug]
 * @param {string} [param.className]
 * @param {object} [param.style]
 */
function HeightElemImpl({ aspectRatio, isVisible = false, debug = false, className = null, style = null }, ref) {
  return (
    <div
      ref={ref}
      style={{
        paddingBottom: `${100 / aspectRatio}%`,
        ...style,
      }}
      className={mergeClassNames([
        styles.heightElem,
        !isVisible ? styles.hidden : null,
        debug ? styles.debug : null,
        className,
      ])}
      role="presentation"
    />
  );
}
export const HeightElem = forwardRef(HeightElemImpl);

/**
 * This component is used to generate scroll distance.
 *
 * If provided with the same image as used with the hero image (or a equeal
 * sized elem) as content it will give a scroll distance equal to the hero
 * image height.
 */
function ScrollDistanceElemImpl(
  {
    image,
    isVisible = false,
    isFullHeight = false,
    snapScroll = false,
    heightMultiplier = 1,
    debug = false,
    className = null,
    style = null,
  },
  ref
) {
  let aspectRatio = image.fluid.aspectRatio * (1 / heightMultiplier);
  style = { minHeight: `calc((100vh - ${headerHeight}px) * ${heightMultiplier})`, ...style };
  return !image || !image.fluid ? null : (
    <HeightElem
      ref={ref}
      aspectRatio={aspectRatio}
      debug={debug}
      style={style}
      className={mergeClassNames([
        styles.scrollDistanceElem,
        snapScroll ? styles.scrollSnap : null,
        isFullHeight ? styles.fullHeight : null,
        className,
      ])}
      isVisible={isVisible}
    />
  );
}
export const ScrollDistanceElem = forwardRef(ScrollDistanceElemImpl);
